import { isEmpty, mapValues, uniq } from "lodash";
import { createSelector } from "reselect";

import { IntegrationType } from "constants/integrations";

import { withRegistration } from "lib/businessRegistrations";
import { getDateFromISO8601DateString } from "lib/timeFormats";

import { getIsSaleyardAdmin } from "../auth";
import { getActiveBusinesses } from "../businesses";
import { getActiveLivestockAgentDeployment } from "../deploymentActiveAgent";
import {
  selectBuyerIdBySaleLotIdLookup,
  selectorInvoiceToBusinessIdBySaleLotIdLookup,
  selectorThirdPartyBusinessIdBySaleLotIdLookup,
  selectVendorIdBySaleLotIdLookup,
} from "../indexes";
import { selectIntegrationBusinessesByBusinessIdByTypeLookup } from "../integrationBusinesses";
import { selectResolvedInterestSettingsByBusinessIdLookup } from "../interest";
import { selectorCategorisedBusinessIdsFromCurrentSaleManualAdjustments } from "../manualAdjustments";
import {
  getBusinesses,
  getBusinessRegistrations,
  getBusinessRelations,
  getProperties,
  getSaleBuyerSplits,
  getSaleVendorSplits,
} from "../root";
import { selectVendorCommissionBandsByBusinessIdLookup } from "../vendorCommissions";

export const BusinessTypes = {
  BUYER: "Buyer",
  BUYER_SPLIT: "Buyer Split",
  INVOICEE: "Invoicee",
  VENDOR: "Vendor",
  VENDOR_SPLIT: "Vendor Split",
  TRANSPORTER: "Transporter",
  THIRD_PARTY: "Third Party",
  SUNDRY_TO: "Sundry To",
  SUNDRY_FROM: "Sundry From",
};

export const selectBusinessesIdsInSaleByType = createSelector(
  [
    selectVendorIdBySaleLotIdLookup,
    selectBuyerIdBySaleLotIdLookup,
    selectorInvoiceToBusinessIdBySaleLotIdLookup,
    selectorThirdPartyBusinessIdBySaleLotIdLookup,
    selectorCategorisedBusinessIdsFromCurrentSaleManualAdjustments,
    getSaleVendorSplits,
    getSaleBuyerSplits,
  ],
  (
    vendorIdBySaleLotIdLookup,
    buyerIdBySaleLotIdLookup,
    invoiceToBusinessIdBySaleLotIdLookup,
    thirdPartyBusinessIdBySaleLotIdLookup,
    categorisedBusinessIdsFromCurrentSaleManualAdjustments,
    saleVendorSplits,
    saleBuyerSplits,
  ) => {
    return {
      [BusinessTypes.BUYER]: uniq(Object.values(buyerIdBySaleLotIdLookup)),
      [BusinessTypes.BUYER_SPLIT]: uniq(
        Object.values(saleBuyerSplits)
          .flatMap(bs => bs.splits.map(s => s.businessId))
          .filter(Boolean),
      ),
      [BusinessTypes.INVOICEE]: uniq(
        Object.values(invoiceToBusinessIdBySaleLotIdLookup),
      ),
      [BusinessTypes.THIRD_PARTY]: uniq(
        Object.values(thirdPartyBusinessIdBySaleLotIdLookup),
      ),
      [BusinessTypes.VENDOR]: uniq(Object.values(vendorIdBySaleLotIdLookup)),
      [BusinessTypes.VENDOR_SPLIT]: uniq(
        Object.values(saleVendorSplits)
          .flatMap(vs => vs.splits.map(s => s.businessId))
          .filter(Boolean),
      ),
      [BusinessTypes.SUNDRY_FROM]: uniq(
        categorisedBusinessIdsFromCurrentSaleManualAdjustments.from.filter(
          Boolean,
        ),
      ),
      [BusinessTypes.SUNDRY_TO]: uniq(
        categorisedBusinessIdsFromCurrentSaleManualAdjustments.to.filter(
          Boolean,
        ),
      ),
    };
  },
);

export const selectBusinessesInSaleByIdLookup = createSelector(
  [
    getBusinesses,
    selectBusinessesIdsInSaleByType,
    selectResolvedInterestSettingsByBusinessIdLookup,
    selectVendorCommissionBandsByBusinessIdLookup,
    getActiveLivestockAgentDeployment,
    selectIntegrationBusinessesByBusinessIdByTypeLookup,
    getBusinessRegistrations,
  ],
  (
    businesses,
    businessIdsByType,
    resolvedInterestSettingsByBusinessId,
    vendorCommissionsByBusinessId,
    activeLivestockAgentDeployment,
    integrationBusinessesByTypeByBusinessIdLookup,
    registrationsById,
  ) => {
    const result = {};

    Object.entries(businessIdsByType).forEach(([businessType, businessIds]) => {
      businessIds.forEach(businessId => {
        const business = businesses[businessId] || {};

        if (isEmpty(business)) {
          return;
        }

        const xeroIntegrationBusiness =
          integrationBusinessesByTypeByBusinessIdLookup[IntegrationType.Xero]?.[
            businessId
          ]?.[0] || null;

        const lastModified = business.lastModified
          ? getDateFromISO8601DateString(business.lastModified)
          : null;

        if (businessId in result) {
          result[businessId].roleInSale.push(businessType);
        } else {
          result[businessId] = {
            business: withRegistration(business, registrationsById),
            roleInSale: [businessType],
            lastModified,
            xeroIntegrationBusiness,
            deployment: activeLivestockAgentDeployment,
            interestSettings: resolvedInterestSettingsByBusinessId[businessId],
            vendorCommissionBands:
              vendorCommissionsByBusinessId[businessId] || [],
          };
        }
      });
    });
    return result;
  },
);

export const getBusinessesInSaleById = businessId => state =>
  selectBusinessesInSaleByIdLookup(state)[businessId];

export const selectBusinessesInSaleAggridData = createSelector(
  [selectBusinessesInSaleByIdLookup],
  businessesInSaleLookup => Object.values(businessesInSaleLookup),
);

export const selectOptionsForBusinessField = createSelector(
  [
    getBusinesses,
    selectVendorIdBySaleLotIdLookup,
    selectBuyerIdBySaleLotIdLookup,
    selectorInvoiceToBusinessIdBySaleLotIdLookup,
    selectorThirdPartyBusinessIdBySaleLotIdLookup,
    selectorCategorisedBusinessIdsFromCurrentSaleManualAdjustments,
    getActiveBusinesses,
    getIsSaleyardAdmin,
    getProperties,
    getBusinessRelations,
    getBusinessRegistrations,
  ],
  (
    businessByIdLookup,
    vendorIdBySaleLotIdLookup,
    buyerIdBySaleLotIdLookup,
    invoiceToBusinessIdBySaleLotIdLookup,
    thirdPartyBusinessIdBySaleLotIdLookup,
    categorisedBusinessIdsFromCurrentSaleManualAdjustments,
    activeBusinessByIdLookup,
    isSaleyardAdmin,
    getPropertyLookup,
    businessRelationsLookup,
    registrationsById,
  ) => {
    // Copies, so the flags below aren't written onto the store's businesses.
    const businesses = mapValues(businessByIdLookup, business =>
      withRegistration(business, registrationsById),
    );

    for (const vendorId of Object.values(vendorIdBySaleLotIdLookup)) {
      if (businesses[vendorId]) {
        businesses[vendorId].isVendor = true;
        businesses[vendorId].inSale = true;
      }
    }

    for (const buyerId of Object.values(buyerIdBySaleLotIdLookup)) {
      if (businesses[buyerId]) {
        businesses[buyerId].isBuyer = true;
        businesses[buyerId].inSale = true;
      }
    }

    for (const invoiceToId of Object.values(
      invoiceToBusinessIdBySaleLotIdLookup,
    )) {
      if (businesses[invoiceToId]) {
        businesses[invoiceToId].invoiceTo = true;
        businesses[invoiceToId].isBuyer = true;
        businesses[invoiceToId].inSale = true;
      }
    }

    for (const thirdPartyId of Object.values(
      thirdPartyBusinessIdBySaleLotIdLookup,
    )) {
      if (businesses[thirdPartyId]) {
        businesses[thirdPartyId].thirdParty = true;
        businesses[thirdPartyId].isBuyer = true;
        businesses[thirdPartyId].inSale = true;
      }
    }

    for (const activeBusinessId of Object.values(activeBusinessByIdLookup)) {
      if (businesses[activeBusinessId]) {
        businesses[activeBusinessId].isActive = true;
      }
    }

    for (const businessRelation of Object.values(businessRelationsLookup)) {
      if (businesses[businessRelation.relatedFromId]) {
        businesses[businessRelation.relatedFromId].isRelatedFrom = true;
      }
      if (businesses[businessRelation.relatedToId]) {
        businesses[businessRelation.relatedToId].isRelatedTo = true;
      }
    }

    for (const manualAdjustmentBusinessIds of Object.values(
      categorisedBusinessIdsFromCurrentSaleManualAdjustments,
    )) {
      // Broken up into from/to, but we will just classify as being involved in sundry.
      for (const manualAdjustmentBusinessId of manualAdjustmentBusinessIds) {
        if (businesses[manualAdjustmentBusinessId]) {
          businesses[manualAdjustmentBusinessId].isSundry = true;
        }
      }
    }

    for (const business of Object.values(businesses)) {
      const inActive = business.isActive ? "" : " (INACTIVE)";
      const code = isSaleyardAdmin
        ? business.shortCodeSaleyard
        : business.shortCode;

      business.properties =
        business?.properties?.map(p => ({
          PIC: getPropertyLookup[p.id]?.PIC || "",
          ...p,
        })) || [];
      business.label = `${code ? `(${code})` : ""} ${business.name}${inActive}`;
    }

    return businesses;
  },
);
